import { Box, Heading, List, ListItem, Stack, Text } from '@chakra-ui/react';
import {
  IBillDiscountNextTier,
  IBillDiscountPromotion,
} from 'interfaces/cart.interface';
import { PromotionDiscountTypeEnum } from 'interfaces/promotion.interface';
import { useTranslation } from 'react-i18next';

type CartBillDiscountPromoProps = {
  billDiscountTotal: number;
  nextTier?: IBillDiscountNextTier | null;
  promotions: IBillDiscountPromotion[];
};

const baht = (value: number) => `฿${Math.round(value).toLocaleString()}`;

const formatDiscount = (value: number, type: PromotionDiscountTypeEnum) =>
  type === PromotionDiscountTypeEnum.PERCENTAGE ? `${value}%` : baht(value);

/**
 * "ส่วนลดท้ายบิล" promotional block shown below the cart/checkout summary:
 * a success banner once a tier is met, an upsell nudge toward the next tier,
 * and a card per active promotion listing its name, headline, and conditions.
 * Renders nothing when there is no active bill-discount promotion.
 */
function CartBillDiscountPromo({
  billDiscountTotal,
  nextTier,
  promotions,
}: CartBillDiscountPromoProps) {
  const { t } = useTranslation('cart');

  if (!promotions || promotions.length === 0) {
    return null;
  }

  return (
    <Box mt={6}>
      <Heading fontSize="xl" fontWeight={500} mb={3}>
        {t('cart.summary.billDiscount.title', {
          // TH: ส่วนลดท้ายบิล
          // EN: Bill Discount
        })}
      </Heading>

      {billDiscountTotal > 0 && (
        <Box
          mb={3}
          px={4}
          py={3}
          bg="primary.50"
          border="1px solid"
          borderColor="primary.200"
          borderRadius="md"
        >
          <Text color="primary.700">
            🎉{' '}
            {t('cart.summary.billDiscount.success', {
              // TH: ยินดีด้วย! ตอนนี้คุณได้รับส่วนลด {{amount}} แล้ว
              // EN: Congrats! You've got {{amount}} off
              amount: baht(billDiscountTotal),
            })}
          </Text>
        </Box>
      )}

      {nextTier && (
        <Box
          mb={3}
          px={4}
          py={3}
          bg="secondary.200"
          border="1px solid"
          borderColor="secondary.600"
          borderRadius="md"
        >
          <Text color="gray.700">
            💡{' '}
            {t('cart.summary.billDiscount.upsell', {
              // TH: ช้อปเพิ่มอีกเพียง {{remaining}} ก็จะครบ {{threshold}} และได้รับส่วนลด {{discount}} ทันที!
              // EN: Spend just {{remaining}} more to reach {{threshold}} and get {{discount}} off!
              remaining: baht(nextTier.remaining),
              threshold: baht(nextTier.threshold),
              discount: formatDiscount(
                nextTier.discountValue,
                nextTier.discountType
              ),
            })}
          </Text>
        </Box>
      )}

      <Stack spacing={3}>
        {promotions.map((promotion) => (
          <Box
            key={promotion.id}
            px={5}
            py={4}
            bg="white"
            border="1px solid #e6e6e6"
            borderRadius="12px"
          >
            <Heading fontSize="lg" fontWeight={500} mb={1}>
              {promotion.name}
            </Heading>
            {promotion.description && (
              <Text color="primary.500" fontWeight={500} mb={3}>
                {promotion.description}
              </Text>
            )}
            <Text fontWeight={500} mb={2}>
              {t('cart.summary.billDiscount.conditionsTitle', {
                // TH: เงื่อนไขส่วนลดท้ายบิล:
                // EN: Bill discount conditions:
              })}
            </Text>
            <List spacing={1} styleType="disc" pl={5}>
              {promotion.tiers.map((tier, index) => (
                <ListItem key={index}>
                  {t('cart.summary.billDiscount.tier', {
                    // TH: ลด {{discount}} เมื่อช้อปครบ {{threshold}}
                    // EN: {{discount}} off when you spend {{threshold}}
                    discount: formatDiscount(
                      tier.discountValue,
                      tier.discountType
                    ),
                    threshold: baht(tier.threshold),
                  })}
                </ListItem>
              ))}
            </List>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

export default CartBillDiscountPromo;
